/* Client helper to submit a delivery order.
 * Posts to the Netlify function, which records the order in Supabase and
 * emails the store owners. No payment is taken — payment happens on delivery. */

import { supabase, isSupabaseConfigured } from './supabaseClient'

export const ORDER_STATUSES = ['pending', 'ready', 'shipped', 'delivered', 'cancelled']
export const PAYMENT_STATUSES = ['unpaid', 'paid', 'cancelled']

export const CARRIERS = [
  { key: 'usps', name: 'USPS' },
  { key: 'ups', name: 'UPS' },
  { key: 'fedex', name: 'FedEx' },
  { key: 'dhl', name: 'DHL' },
  { key: 'other', name: 'Other' },
]

/** Public tracking URL for a stored carrier name + number (for admin display). */
export function trackingUrl(carrier, number) {
  if (!number) return null
  const c = String(carrier || '').toLowerCase()
  const n = encodeURIComponent(number)
  if (c.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`
  if (c.includes('ups')) return `https://www.ups.com/track?tracknum=${n}`
  if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${n}`
  if (c.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${n}`
  return null
}

/** Admin: mark an order shipped, save tracking, and email the customer.
 *  Goes through an admin-verified Netlify function (needs the session token). */
export async function notifyShipment({ orderId, carrier, trackingNumber }) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const res = await fetch('/.netlify/functions/notify-shipment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      access_token: session?.access_token,
      order_id: orderId,
      carrier,
      tracking_number: trackingNumber,
    }),
  })
  let data = null
  try {
    data = await res.json()
  } catch {
    /* ignore */
  }
  if (!res.ok) throw new Error(data?.error || 'Could not send the tracking email.')
  return data
}

/** Upload a delivery-confirmation photo (courier snaps it on their phone).
 *  Stored in the product-images bucket under delivery/. Returns the public URL. */
export async function uploadDeliveryPhoto(file, orderNumber = 'order') {
  if (!isSupabaseConfigured) throw new Error('Supabase is not configured.')
  if (!file || !file.type.startsWith('image/')) throw new Error('Please choose an image.')
  if (file.size > 10 * 1024 * 1024) throw new Error('Image is too large (max 10 MB).')
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
  const safe = String(orderNumber).replace(/[^a-z0-9-]/gi, '').toLowerCase() || 'order'
  const path = `delivery/${safe}-${Date.now()}.${ext}`
  const { error } = await supabase.storage
    .from('product-images')
    .upload(path, file, { cacheControl: '3600', upsert: true })
  if (error) throw error
  const { data } = supabase.storage.from('product-images').getPublicUrl(path)
  return data.publicUrl
}

/** Admin: mark an order delivered with a photo and email the customer.
 *  Goes through an admin-verified Netlify function. */
export async function notifyDelivered({ orderId, photoUrl }) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const res = await fetch('/.netlify/functions/notify-delivered', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      access_token: session?.access_token,
      order_id: orderId,
      photo_url: photoUrl,
    }),
  })
  let data = null
  try {
    data = await res.json()
  } catch {
    /* ignore */
  }
  if (!res.ok) throw new Error(data?.error || 'Could not send the delivery email.')
  return data
}

/** Admin: list every order, newest first. Requires an admin session (RLS). */
export async function adminListOrders() {
  if (!isSupabaseConfigured) throw new Error('Supabase is not configured.')
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

/** Admin: change an order's status (e.g. pending → delivered). */
export async function updateOrderStatus(id, status) {
  if (!isSupabaseConfigured) throw new Error('Supabase is not configured.')
  const { data, error } = await supabase
    .from('orders')
    .update({ status })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

/** Admin: mark an order's affiliate commission paid/unpaid. Goes through an
 *  admin-verified function that updates the flag and — when marking paid —
 *  emails the sales rep that they've been paid. */
export async function markCommissionPaid({ orderId, paid }) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const res = await fetch('/.netlify/functions/notify-commission-paid', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ access_token: session?.access_token, order_id: orderId, paid }),
  })
  let data = null
  try {
    data = await res.json()
  } catch {
    /* ignore */
  }
  if (!res.ok) throw new Error(data?.error || 'Could not update commission status.')
  return data
}

/** Admin: change an order's payment status (unpaid → paid, etc.). */
export async function updateOrderPaymentStatus(id, payment_status) {
  if (!isSupabaseConfigured) throw new Error('Supabase is not configured.')
  const { data, error } = await supabase
    .from('orders')
    .update({ payment_status })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

/** Submit a delivery availability inquiry (email + zip). Records it and
 *  notifies the owners; does not email the person who inquired. */
export async function submitDeliveryInquiry({ email, zip }) {
  const res = await fetch('/.netlify/functions/submit-delivery-inquiry', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, zip }),
  })
  let data = null
  try {
    data = await res.json()
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    throw new Error(data?.error || 'Could not submit. Please try again.')
  }
  return data
}

/** Create a Stripe PaymentIntent for the embedded card flow. Server re-prices
 *  the cart and returns a client_secret to confirm on-page; the order is
 *  recorded by the webhook once payment succeeds. Returns
 *  { clientSecret, order_number, amount }. */
export async function createPaymentIntent({
  customer,
  items,
  userId,
  referralCode,
  fulfillment,
  zip,
  smsConsent,
}) {
  const res = await fetch('/.netlify/functions/create-payment-intent', {
    method: 'POST',
    headers: await checkoutHeaders(),
    body: JSON.stringify({
      customer,
      items: items.map((i) => ({ id: i.id, name: i.name, qty: i.qty, price: i.price })),
      user_id: userId || null,
      referral_code: referralCode || null,
      fulfillment: fulfillment || 'delivery',
      zip: zip || null,
      sms_consent: Boolean(smsConsent),
    }),
  })
  let data = null
  try {
    data = await res.json()
  } catch {
    /* ignore */
  }
  if (!res.ok || !data?.clientSecret) {
    const err = new Error(data?.error || 'Could not start payment.')
    err.notConfigured = res.status === 503
    throw err
  }
  return data
}

/** Charge a card online via TagadaPay (server verifies price, records the paid
 *  order, and emails owner + customer). `tagadaToken` comes from the browser
 *  tokenizer so the PAN never reaches our server. Returns the function payload:
 *  { ok, order_number, total, ... } on success, or { requireAction:'redirect',
 *  redirectUrl } when the card needs extra 3-D Secure authentication. */
export async function processPayment({
  customer,
  items,
  userId,
  referralCode,
  tagadaToken,
  scaRequired,
  sessionData,
  fulfillment,
  zip,
  smsConsent,
}) {
  const res = await fetch('/.netlify/functions/process-payment', {
    method: 'POST',
    headers: await checkoutHeaders(),
    body: JSON.stringify({
      customer,
      items: items.map((i) => ({ id: i.id, name: i.name, qty: i.qty, price: i.price })),
      user_id: userId || null,
      referral_code: referralCode || null,
      tagadaToken,
      scaRequired: Boolean(scaRequired),
      sessionData: sessionData || null,
      fulfillment: fulfillment || 'delivery',
      zip: zip || null,
      sms_consent: Boolean(smsConsent),
    }),
  })

  let data = null
  try {
    data = await res.json()
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const err = new Error(data?.error || 'Your payment could not be processed.')
    err.notConfigured = res.status === 503
    throw err
  }
  return data
}

export async function submitOrder({ customer, items, userId, referralCode, smsConsent }) {
  const res = await fetch('/.netlify/functions/submit-order', {
    method: 'POST',
    headers: await checkoutHeaders(),
    body: JSON.stringify({
      customer,
      items: items.map((i) => ({
        id: i.id,
        name: i.name,
        qty: i.qty,
        price: i.price,
      })),
      user_id: userId || null,
      referral_code: referralCode || null,
      sms_consent: Boolean(smsConsent),
    }),
  })

  let data = null
  try {
    data = await res.json()
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    throw new Error(data?.error || 'Could not submit your order. Please try again.')
  }
  return data
}

/** Checkout requests carry the signed-in user's access token so the server can
 *  confirm the account has been approved (compliance gate). */
async function checkoutHeaders() {
  const headers = { 'Content-Type': 'application/json' }
  try {
    const { data } = await supabase.auth.getSession()
    const token = data?.session?.access_token
    if (token) headers.Authorization = `Bearer ${token}`
  } catch {
    /* not signed in — the server will refuse the checkout */
  }
  return headers
}
